import { Common } from '@stockbit/ui';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo } from 'react';
import Lottie from 'lottie-web';
import { getActiveCategory, getStreamCategories } from '../utils';
import NavigationDropdown from './NavigationDropdown';
import { DropdownOption, MobileContainer, PopoverContainer } from './styled';
import { STREAM_STATE, STREAM_TYPE } from '../constants';
import streamWidget, { selectors } from '../slice';
import lottieeuforia from '../../../../public/animation/lottie-euforia.json';
import { DATA_CY_DROPDOWN_STREAM_ACTIVE } from './NavigationTab/__tests__/constant';

const { Popover } = Common;

const LOTTIE_TAB_ANIMATION = 'lottie-tab-animation-mobile';

interface MobileProps {
  streamType: string;
  hasDefaultTab: boolean;
  isSelfStream?: boolean;
}

function Mobile({ streamType, hasDefaultTab, isSelfStream = false }: MobileProps) {
  const dispatch = useDispatch();

  const {
    filterParams: { category },
    hasNewResearch,
  } = useSelector(selectors);

  const streamCategories = useMemo(() => {
    const listCategories = getStreamCategories(streamType);

    if (streamType === STREAM_TYPE.USER && !isSelfStream) {
      const result = listCategories.filter((item) => item.key !== 'notes');
      return result;
    }

    return listCategories;
  }, [streamType, isSelfStream]);

  const { label: activeCategory } = getActiveCategory(
    category,
    streamCategories,
  );

  const handleChangeCategory = (key: string) => {
    dispatch(
      // @ts-ignore
      streamWidget.actions.setFilterParam({
        category: key,
      }),
    );

    // destroy lottie when click research tab
    if (hasNewResearch && key === 'research') {
      dispatch(streamWidget.actions.removeHasNewResearch());
      Lottie.destroy(LOTTIE_TAB_ANIMATION);
      document.getElementById('navtab-mobile-research').remove();
    }

    if (hasDefaultTab) {
      localStorage.setItem(STREAM_STATE.CATEGORY, key);
    }
  };

  useEffect(() => {
    Lottie.loadAnimation({
      name: LOTTIE_TAB_ANIMATION,
      container: document.getElementById('navtab-mobile-research'),
      renderer: 'svg',
      loop: true,
      autoplay: true,
      animationData: lottieeuforia,
    });

    return () => {
      Lottie.destroy(LOTTIE_TAB_ANIMATION);
    };
  }, [hasNewResearch, category]);

  const showResearchIndicator = hasNewResearch && category !== 'research';

  return (
    <MobileContainer>
      <Popover
        content={() => (
          <PopoverContainer>
            {streamCategories.map(
              ({ label, key, dataCy }: { [key: string]: string }) => (
                <DropdownOption
                  key={key}
                  onClick={() => handleChangeCategory(key)}
                  style={{ position: 'relative' }}
                  data-cy={dataCy}
                >
                  {label}
                  {key === 'research' && showResearchIndicator && (
                    <span
                      id={`navtab-mobile-${key}`}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: -4,
                        width: '72px',
                      }}
                    />
                  )}
                </DropdownOption>
              ),
            )}
          </PopoverContainer>
        )}
        trigger="click"
        placement="bottomLeft"
        overlayInnerStyle={{ borderRadius: '4px' }}
      >
        <NavigationDropdown data-cy={DATA_CY_DROPDOWN_STREAM_ACTIVE}>
          {activeCategory}
        </NavigationDropdown>
      </Popover>
    </MobileContainer>
  );
}

export default Mobile;
